import dayjs from 'dayjs'
import { getPractitionerDisplayName, getPractitionerName } from '@/utils/providerUtils'
import type { PractitionerThreadOption } from './service'

const AVATAR_COLORS = [
  '#ef4444',
  '#f97316',
  '#3b82f6',
  '#8b5cf6',
  '#10b981',
  '#6366f1',
  '#ec4899',
  '#14b8a6',
]

/** Stable per-practitioner avatar color, so the same person is always the same hue. */
export function avatarColor(seed: string): string {
  const hash = seed.split('').reduce((acc, character) => acc + character.charCodeAt(0), 0)
  return AVATAR_COLORS[hash % AVATAR_COLORS.length]
}

export function getInitials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase()
}

export { getPractitionerName }

export function getParticipantDisplayName(
  participant: PractitionerThreadOption | undefined,
): string {
  return getPractitionerDisplayName(participant?.practitioner)
}

/**
 * Everyone on the thread except the current user, in a stable order: the
 * creator first, then the recipients as stored.
 *
 * A thread's creator lives in `sender` and the rest in `recipients`, so callers
 * that want "who am I talking to" have to merge both — and must not assume there
 * is only one.
 */
export function getOtherParticipants(
  thread: { sender?: PractitionerThreadOption; recipients: PractitionerThreadOption[] },
  currentPractitionerId: string | undefined,
): PractitionerThreadOption[] {
  return [thread.sender, ...thread.recipients].filter(
    (participant): participant is PractitionerThreadOption =>
      Boolean(
        participant?.practitioner?.id && participant.practitioner.id !== currentPractitionerId,
      ),
  )
}

/**
 * Compact participant summary for a row or header: `A`, `A, B`, `A, B +2`.
 *
 * Names are shown in full up to `maxNames` so a 1:1 conversation is unchanged,
 * and the remainder collapses to a count rather than truncating mid-name.
 */
export function formatParticipantList(
  participants: PractitionerThreadOption[],
  maxNames = 2,
): string {
  if (participants.length === 0) {
    return 'No other participants'
  }

  const names = participants.slice(0, maxNames).map(getParticipantDisplayName)
  const remaining = participants.length - names.length

  return remaining > 0 ? `${names.join(', ')} +${remaining}` : names.join(', ')
}

/** Prose list for copy — `A`, `A and B`, `A, B and C`. */
export function formatNameList(names: string[]): string {
  if (names.length <= 1) {
    return names[0] ?? ''
  }

  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

/** Compact last-activity stamp for a conversation row: 10:18 PM / Yesterday / Monday / Mar 4. */
export function formatThreadTime(iso: string | undefined): string {
  if (!iso) {
    return ''
  }

  const value = dayjs(iso)
  const today = dayjs().startOf('day')

  if (value.isAfter(today)) {
    return value.format('h:mm A')
  }
  if (value.isAfter(today.subtract(1, 'day'))) {
    return 'Yesterday'
  }
  if (value.isAfter(today.subtract(7, 'day'))) {
    return value.format('dddd')
  }

  return value.format('MMM D')
}

/** Label for a date separator between message groups. */
export function formatDateSeparator(iso: string | undefined): string {
  if (!iso) {
    return ''
  }

  const value = dayjs(iso).startOf('day')
  const today = dayjs().startOf('day')

  if (value.isSame(today)) {
    return 'Today'
  }
  if (value.isSame(today.subtract(1, 'day'))) {
    return 'Yesterday'
  }
  if (value.isAfter(today.subtract(7, 'day'))) {
    return value.format('dddd')
  }
  if (value.isSame(today, 'year')) {
    return value.format('MMMM D')
  }

  return value.format('MMMM D, YYYY')
}

/** Day key used to decide where a date separator goes. */
export function messageDayKey(iso: string | undefined): string {
  return iso ? dayjs(iso).format('YYYY-MM-DD') : ''
}
